/** @jsxImportSource ./simboluri-jsx */
// «PLAN DE SĂPĂTURĂ» — vederea Studioului cu colile de săpătură ale soluției:
// planul săpăturii generale (groapa cutiei de subsol cu taluzul ei, șanțurile
// de sub axe, cotele, cele două secțiuni rabatate pe plan, notele de execuție)
// și coala secțiunilor la 1:50 cu straturile terenului, desenate de server din
// blocul `sapatura` al modelului Pasului 7 (`structure/planse/sapatura.py`),
// vizorul colii alese și descărcarea ei — sau a setului — ca DXF / DWG.
//
// Aceeași mecanică ca planul de cofraj: coala aleasă stă în URL (`?coala=`),
// desenele vin pe versiunea setului, clic pe un șanț duce la pagina axului lui.

import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useT } from "../i18n";
import {
  getPlanSapaturaColi, planSapaturaDesenUrl, type ColaPlanArmare, type PlanArmareColi,
} from "./apiStructura";
import { DrawingView } from "./DwgViewer";
import { hashElement } from "./legaturi";
import { Descarcari } from "./PlanArmare";

/** Id-urile de pe colile de săpătură care duc la o pagină de element: axele
 *  șanțurilor și elementele nivelului de jos — nu groapa, terenul, secțiunile
 *  sau notele. */
export function esteElement(id: string): boolean {
  return !["groapa", "ctn", "note"].includes(id) && !id.startsWith("sectiune:");
}

export default function PlanSapatura({ projectId, cloneId, solutie }: {
  projectId: string;
  /** spațiul din care se citesc planșele (`useStructure().cloneId`) */
  cloneId: string | null;
  /** referința rândului privit: din ea se fac linkurile spre paginile elementelor */
  solutie?: string | null;
}) {
  const t = useT();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  // `undefined` = cererea e pe drum; `null` = spațiul n-are planșe
  const [set, setSet] = useState<PlanArmareColi | null | undefined>(undefined);

  useEffect(() => {
    if (cloneId == null) {
      setSet(null);
      return;
    }
    let viu = true;
    setSet(undefined);
    getPlanSapaturaColi(projectId, cloneId)
      .then((c) => { if (viu) setSet(c); })
      .catch(() => { if (viu) setSet(null); });
    return () => { viu = false; };
  }, [projectId, cloneId]);

  const coli: ColaPlanArmare[] = set?.coli ?? [];
  const ceruta = params.get("coala");
  const coala = coli.find((c) => c.id === ceruta) ?? coli[0] ?? null;
  const alege = (id: string) => {
    const q = new URLSearchParams(params);
    q.set("coala", id);
    setParams(q, { replace: true });
  };
  const dwgOk = set?.dwg_disponibil !== false;

  return (
    <div className="pj-step-page" data-testid="plan-sapatura">
      <h2>{t("struct.psap.titlu")}</h2>
      {set === undefined ? (
        <div className="sg-muted">{t("comun.se_incarca")}</div>
      ) : !coli.length ? (
        <div className="sg-muted" style={{ marginTop: 10 }}>{t("struct.psap.gol")}</div>
      ) : (
        <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
          <aside className="ob-card" style={{ width: 260, flex: "none", padding: "10px 12px" }}
                 data-testid="lista-coli-sapatura">
            <div style={{ borderBottom: "1px solid var(--line)", paddingBottom: 10,
                          marginBottom: 8 }}>
              <Descarcari projectId={projectId} cloneId={cloneId} coala={coala?.id ?? null}
                          dwgOk={dwgOk} set="plan_sapatura" />
            </div>
            <div className="ob-lbl" style={{ marginBottom: 4 }}>{t("struct.psap.coli")}</div>
            {coli.map((c) => {
              const on = coala?.id === c.id;
              return (
                <button key={c.id} type="button"
                        className={`ob-coala${on ? " on" : ""}`}
                        aria-pressed={on}
                        title={c.titlu}
                        onClick={() => alege(c.id)}>
                  {c.titlu}
                </button>
              );
            })}
          </aside>

          <div style={{ flex: 1, minWidth: 0 }}>
            {coala && (
              <div className="ob-card" style={{ overflow: "hidden" }}>
                <div style={{ height: "calc(100vh - 210px)", minHeight: 460 }}>
                  <DrawingView
                    key={coala.id}
                    src={planSapaturaDesenUrl(projectId, cloneId, coala.id)}
                    versiune={set?.versiune}
                    title={coala.titlu}
                    embed
                    faraAntet
                    faraBusola
                    onJump={(id) => { if (id && esteElement(id)) navigate(hashElement(projectId, id, solutie)); }}
                    linkEticheta={t("struct.pa.link_element")}
                  />
                </div>
                {coala.elemente.some(esteElement) && (
                  <div style={{ padding: "10px 14px", borderTop: "1px solid var(--line)",
                                display: "flex", gap: 6, flexWrap: "wrap" }}>
                    {coala.elemente.filter(esteElement).map((ref) => (
                      <a key={ref} className="ob-chip ob-c-mut" style={{ textDecoration: "none" }}
                         href={hashElement(projectId, ref, solutie)}>
                        {ref}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
